const KEY_MAP: Record<string, string> = {
  ArrowUp: 'ArrowUp', ArrowDown: 'ArrowDown',
  ArrowLeft: 'ArrowLeft', ArrowRight: 'ArrowRight',
  Space: ' ', Digit1: '1', Digit2: '2', Digit3: '3', Digit4: '4',
};

// Direct shared state for scenes to poll. Synthetic KeyboardEvent doesn't
// reliably reach Phaser 3's KeyboardManager on real mobile (event order +
// pointerType filters vary), so scenes read this object as a fallback in
// addition to cursor.isDown. See horizontal-battle-scene.ts _handleInput.
export interface TouchInputState {
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
  skill1: boolean;
  skill2: boolean;
  skill3: boolean;
  skill4: boolean;
  space: boolean;
  // 2026-06-14 老闆「左邊虛擬把手」: analog joystick. moveX/moveY are the unit
  // vector (-1..1) from the joystick base centre; usingJoystick is true while a
  // finger is actively driving the stick. The 8-way booleans above are kept in
  // sync (threshold) so existing keyboard-style readers + tests still work.
  moveX: number;
  moveY: number;
  usingJoystick: boolean;
}
declare global {
  interface Window { __touchInput?: TouchInputState }
}
function ensureGlobalState(): TouchInputState {
  if (!window.__touchInput) {
    window.__touchInput = { up: false, down: false, left: false, right: false, skill1: false, skill2: false, skill3: false, skill4: false, space: false, moveX: 0, moveY: 0, usingJoystick: false };
  }
  // Defensive: older cached state objects may predate the joystick fields.
  const s = window.__touchInput;
  if (typeof s.moveX !== 'number') s.moveX = 0;
  if (typeof s.moveY !== 'number') s.moveY = 0;
  if (typeof s.usingJoystick !== 'boolean') s.usingJoystick = false;
  return s;
}

/** Result of mapping a raw joystick displacement to game input. */
export interface JoystickVector {
  moveX: number;
  moveY: number;
  up: boolean;
  down: boolean;
  left: boolean;
  right: boolean;
}

/**
 * Pure mapping: raw knob displacement (dx, dy in px, screen coords where +y is
 * DOWN) → analog unit vector + 8-way booleans. Exported for unit testing.
 *
 *  - Normalises by `radius` and clamps each axis to [-1, 1] (knob can't exceed
 *    the base edge, but callers may pass un-clamped px).
 *  - Inside `deadzone` (fraction of radius, ~0.18) the stick reads as centred
 *    → all zero / false (kills jitter from a resting thumb).
 *  - Booleans fire when an axis magnitude crosses the deadzone threshold.
 *
 * radius<=0 → treated as no input (defensive; avoids div-by-zero).
 */
export function joystickVector(dx: number, dy: number, radius: number, deadzone = 0.18): JoystickVector {
  const zero: JoystickVector = { moveX: 0, moveY: 0, up: false, down: false, left: false, right: false };
  if (!(radius > 0) || !Number.isFinite(dx) || !Number.isFinite(dy)) return zero;
  const clamp1 = (v: number) => Math.max(-1, Math.min(1, v));
  const nx = clamp1(dx / radius);
  const ny = clamp1(dy / radius);
  // Deadzone on overall magnitude so a near-centre thumb reads as no input.
  const mag = Math.hypot(nx, ny);
  if (mag < deadzone) return zero;
  return {
    moveX: nx,
    moveY: ny,
    left: nx <= -deadzone,
    right: nx >= deadzone,
    up: ny <= -deadzone,
    down: ny >= deadzone,
  };
}
// Only the boolean fields are mapped from key codes (moveX/moveY/usingJoystick
// are driven by the joystick, never by a key) — narrow so state[field] = bool
// type-checks against the now-mixed TouchInputState.
type BoolKey = { [K in keyof TouchInputState]: TouchInputState[K] extends boolean ? K : never }[keyof TouchInputState];
const CODE_TO_FIELD: Record<string, BoolKey> = {
  ArrowUp: 'up', ArrowDown: 'down', ArrowLeft: 'left', ArrowRight: 'right',
  Digit1: 'skill1', Digit2: 'skill2', Digit3: 'skill3', Digit4: 'skill4', Space: 'space',
};

function dispatchKey(type: 'keydown' | 'keyup', key: string, code: string): void {
  // Update direct shared state (Phaser scenes poll this).
  const state = ensureGlobalState();
  const field = CODE_TO_FIELD[code];
  if (field) state[field] = (type === 'keydown');
  // Also fire synthetic KeyboardEvent for keyboard-based code paths and
  // legacy listeners. Phaser 3 KeyboardManager attaches to window.
  window.dispatchEvent(new KeyboardEvent(type, { key, code, bubbles: true }));
  document.dispatchEvent(new KeyboardEvent(type, { key, code, bubbles: true }));
}

/**
 * P06 (iPad Multi-touch) — touch HUD that lets the kid run the left-cluster
 * (move ◀▶) AND right-cluster (skills 1-4 + space) SIMULTANEOUSLY with two
 * different fingers. Each pointer is tracked independently via PointerEvent's
 * pointerId so:
 *
 *   - Pressing ◀ with one finger + pressing 1 with another finger → both
 *     fire keydown (kid moves left while casting skill 1).
 *   - Releasing the ◀ finger sends ONLY ArrowLeft keyup; the skill finger
 *     keeps key 1 held down until it lifts.
 *   - Two fingers on the same button → one keydown on first press; key
 *     stays down until ALL fingers on that button release (refcount).
 *   - Finger sliding off the button while still down → keyup (defensive,
 *     avoids stuck key).
 *
 * Fallback: if PointerEvents aren't supported (or `ontouchstart` is absent
 * AND maxTouchPoints=0) we return a no-op unmount. Desktop keyboard input is
 * unaffected — this layer only DISPATCHES key events on top of normal
 * keyboard handling.
 *
 * Spec: docs/GAME_TECHNIQUES_WIKI.md §P06.
 */
export function mountTouchControls(): () => void {
  const supportsTouch = 'ontouchstart' in window || navigator.maxTouchPoints > 0;
  if (!supportsTouch) return () => {};

  // Idempotent guard — 2026-05-29 mobile-touch fix.
  // main.ts setMode() unmounts on transition, but scene-internal hops
  // (Hub→Battle via scene.start) bypass setMode and leave the previous
  // mount in place. If a second mount call fires while a root is already
  // in the DOM, return a no-op unmount so we don't end up with duplicate
  // overlapping button overlays (which would also double-fire keydowns).
  if (document.querySelector('.touch-controls')) {
    return () => {};
  }

  const root = document.createElement('div');
  root.className = 'touch-controls';
  // 2026-06-05 老闆「既然可以觸控,把虛擬鍵盤拿掉」:移動改用「手指拖曳畫面」
  // (HorizontalBattleScene 的 pointer-follow),大廳改「直接點建築物」進關。
  // 2026-06-05 老闆「右邊數字鍵能刪掉嗎」:移除 1/2/3/4 數字格(slot 2-4 目前未
  // 解鎖、純佔畫面),只留兩顆小鈕——「技」= 主動技能(slot 0 / Digit1)、
  // 「⚡」= 大絕(Space,擊殺充能滿才放)。平板無實體鍵盤,這兩顆是唯一施放入口。
  // 2026-06-14 老闆「手機能不能在左邊有個虛擬把手 直接手觸控戰車會擋住畫面」:
  //   左下角虛擬搖桿(base 圓底座 + knob 中央把手)。手指在左下角操桿,戰車照桿的
  //   方向/力度移動,手指不再壓在戰車上擋畫面。映射見 joystickVector()。
  // boss 2026-06-17「兩個都同時支援」:搖桿照掛,同時 scene 的 pointer-follow 也生效
  // (按搖桿外的畫面 → 戰車朝手指跑)。兩種操作並存,玩家用哪個都行。
  root.innerHTML = `
    <div class="tc-joystick" aria-hidden="true">
      <div class="tc-joystick-knob"></div>
    </div>
    <div class="tc-right-cluster">
      <button class="tc-btn tc-skill" data-key="Digit1">技</button>
      <button class="tc-btn tc-space" data-key="Space">⚡</button>
    </div>
  `;
  document.body.appendChild(root);

  // ── Virtual joystick (left-bottom) ──────────────────────────────────────
  // pointerdown anywhere on the base → grab the stick; pointermove drags the
  // knob (clamped to the base radius); release → recentre + zero input. We
  // capture the pointer + preventDefault + touch-action:none so the Phaser
  // canvas underneath never also receives this pointer (no double drag).
  const joyBase = root.querySelector('.tc-joystick') as HTMLElement | null;
  const joyKnob = root.querySelector('.tc-joystick-knob') as HTMLElement | null;
  let joyPointerId: number | null = null;
  let joyCenterX = 0, joyCenterY = 0, joyRadius = 56;

  function setJoystickState(v: JoystickVector): void {
    const s = ensureGlobalState();
    s.moveX = v.moveX; s.moveY = v.moveY;
    s.up = v.up; s.down = v.down; s.left = v.left; s.right = v.right;
    s.usingJoystick = (v.moveX !== 0 || v.moveY !== 0);
  }

  function resetJoystick(): void {
    joyPointerId = null;
    const s = ensureGlobalState();
    s.moveX = 0; s.moveY = 0;
    s.up = false; s.down = false; s.left = false; s.right = false;
    s.usingJoystick = false;
    if (joyKnob) { joyKnob.style.transform = 'translate(0px, 0px)'; }
  }

  function updateKnob(clientX: number, clientY: number): void {
    let dx = clientX - joyCenterX;
    let dy = clientY - joyCenterY;
    // Clamp the visual knob to the base edge.
    const dist = Math.hypot(dx, dy);
    if (dist > joyRadius && dist > 0) {
      dx = (dx / dist) * joyRadius;
      dy = (dy / dist) * joyRadius;
    }
    if (joyKnob) { joyKnob.style.transform = `translate(${dx}px, ${dy}px)`; }
    setJoystickState(joystickVector(dx, dy, joyRadius));
  }

  if (joyBase) {
    const joyDown = (e: Event) => {
      e.preventDefault();
      const pe = e as PointerEvent;
      const rect = joyBase.getBoundingClientRect();
      joyCenterX = rect.left + rect.width / 2;
      joyCenterY = rect.top + rect.height / 2;
      joyRadius = Math.max(1, rect.width / 2);
      joyPointerId = (typeof pe.pointerId === 'number') ? pe.pointerId : 0;
      try {
        if (typeof pe.pointerId === 'number' && typeof joyBase.setPointerCapture === 'function') {
          joyBase.setPointerCapture(pe.pointerId);
        }
      } catch { /* unsupported in test harness — ignore */ }
      const cx = (typeof pe.clientX === 'number') ? pe.clientX : joyCenterX;
      const cy = (typeof pe.clientY === 'number') ? pe.clientY : joyCenterY;
      updateKnob(cx, cy);
    };
    const joyMove = (e: Event) => {
      if (joyPointerId === null) return;
      const pe = e as PointerEvent;
      if (typeof pe.pointerId === 'number' && pe.pointerId !== joyPointerId) return;
      e.preventDefault();
      const cx = (typeof pe.clientX === 'number') ? pe.clientX : joyCenterX;
      const cy = (typeof pe.clientY === 'number') ? pe.clientY : joyCenterY;
      updateKnob(cx, cy);
    };
    const joyUp = (e: Event) => {
      const pe = e as PointerEvent;
      if (joyPointerId !== null && typeof pe.pointerId === 'number' && pe.pointerId !== joyPointerId) return;
      resetJoystick();
    };
    joyBase.addEventListener('pointerdown', joyDown);
    joyBase.addEventListener('pointermove', joyMove);
    joyBase.addEventListener('pointerup', joyUp);
    joyBase.addEventListener('pointercancel', joyUp);
    joyBase.addEventListener('pointerleave', joyUp);
  }

  // P06: pointerId → { code, btn } tracking. A held key remains down until
  // every pointer that pressed any button mapped to that code releases.
  // pointerKeyRefcount counts pressed pointers per code so multiple fingers
  // on different buttons sharing the same code don't drop the key early.
  const pointerToCode = new Map<number, string>();
  const pointerKeyRefcount = new Map<string, number>();

  function acquireKey(pointerId: number, code: string): void {
    if (pointerToCode.has(pointerId)) return; // already pressed by this pointer
    pointerToCode.set(pointerId, code);
    const prev = pointerKeyRefcount.get(code) ?? 0;
    pointerKeyRefcount.set(code, prev + 1);
    if (prev === 0) {
      dispatchKey('keydown', KEY_MAP[code], code);
    }
  }

  function releaseKey(pointerId: number): void {
    const code = pointerToCode.get(pointerId);
    if (!code) return;
    pointerToCode.delete(pointerId);
    const remaining = (pointerKeyRefcount.get(code) ?? 1) - 1;
    if (remaining <= 0) {
      pointerKeyRefcount.delete(code);
      dispatchKey('keyup', KEY_MAP[code], code);
    } else {
      pointerKeyRefcount.set(code, remaining);
    }
  }

  for (const btn of root.querySelectorAll('.tc-btn')) {
    const el = btn as HTMLElement;
    const code = el.dataset.key!;
    const down = (e: Event) => {
      e.preventDefault();
      const pe = e as PointerEvent;
      // pointerId may be undefined when synthesised via dispatchEvent in
      // jsdom (test harness). Fall back to a synthetic id derived from the
      // button so each unique button still maps to a single key press.
      const pid = (typeof pe.pointerId === 'number')
        ? pe.pointerId
        : -1 - Array.from(root.querySelectorAll('.tc-btn')).indexOf(el);
      // Capture so subsequent pointerup goes to this element even if the
      // finger drifts off — prevents stuck keys on edge-of-button release.
      try {
        if (typeof pe.pointerId === 'number' && typeof el.setPointerCapture === 'function') {
          el.setPointerCapture(pe.pointerId);
        }
      } catch { /* unsupported in test harness — ignore */ }
      acquireKey(pid, code);
    };
    const up = (e: Event) => {
      e.preventDefault();
      const pe = e as PointerEvent;
      const pid = (typeof pe.pointerId === 'number')
        ? pe.pointerId
        : -1 - Array.from(root.querySelectorAll('.tc-btn')).indexOf(el);
      releaseKey(pid);
    };
    el.addEventListener('pointerdown', down);
    el.addEventListener('pointerup', up);
    el.addEventListener('pointercancel', up);
    el.addEventListener('pointerleave', up);  // finger slid off
  }

  return () => {
    // Release any in-flight pointers so we don't leave keys stuck if the
    // scene unmounts mid-press (e.g. ESC out of battle while holding ◀).
    for (const pid of Array.from(pointerToCode.keys())) releaseKey(pid);
    // Zero out joystick state so a scene transition mid-drag doesn't leave the
    // tank coasting (usingJoystick/moveX stuck) into the next scene.
    resetJoystick();
    root.remove();
  };
}
